import { useMemo } from 'react';
import { useMatch, Link } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';

import FooterBg from '@/assets/images/onboarding/footer-wave.png';
import FooterMobileBg from '@/assets/images/onboarding/footer-wave-mobile.png';
import Chat from '@/assets/icons/chat-1.svg'
import MoneyHand from '@/assets/icons/money-hand.svg'
import CheckVerified from '@/assets/icons/check-verified.svg'

import { ROUTES } from '@/shared/constants/routes';
import { onBoardingFixedBlock } from '@/store/slices/onboarding/selectors';
import { useAppSelector } from '@/shared/hooks/store';
import PrivacyChoices from '@/assets/images/privacy-choices.svg';
import { selectAuthState } from '../../../../store/slices/auth/selectors';
import { useChatClassName } from '@/shared/hooks/useChatClassName';

interface Props {
    ivoryBg?: boolean;
}

interface FooterInfoItemProps {
    icon: string;
    iconAlt: string;
    title: string;
    titleTestId?: string;
    children: React.ReactNode;
}

const FooterInfoItem = ({ icon, iconAlt, title, titleTestId, children }: FooterInfoItemProps) => (
    <div className="flex-1">
        <div className="flex flex-row items-start justify-start lg:justify-center">
            <div className="mr-[21px] flex h-[50px] w-[50px] shrink-0 rounded-lg bg-mint-200 shadow-footerIcon lg:h-[75px] lg:w-[75px]">
                <img src={icon} alt={iconAlt} className="p-2.5 lg:p-4" />
            </div>
            <div className="font-dmSans flex flex-col gap-[8px] lg:gap-[7px]">
                <span className="font-dmSansBold text-17 md:text-20" data-testid={titleTestId}>{title}</span>
                {children}
            </div>
        </div>
    </div>
);

const links = [
    { label: 'Privacy Policy', link: 'https://medbox.com/privacy-policy/' },
    { label: 'Terms of Use', link: 'https://medbox.com/terms-of-use/' },
    { label: 'Cookie Policy', link: 'https://medbox.com/cookie-policy/' },
    { label: 'Notice of Privacy', link: 'https://medbox.com/notice-of-privacy-practices/' },
    { label: 'Child Safety Waiver', link: 'https://medbox.com/child-safety-waiver/' },
    { label: 'Privacy Choices', link: 'https://medbox.com/your-privacy-choices/', external: true, customClass: 'cky-banner-element', img: PrivacyChoices }, // feat: adding the revisit classname tagged trigger
];


const Footer = ({ ivoryBg }: Props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();
    const authorizedId = useAppSelector(selectAuthState)?.id;
    const isOnboarding = useMatch(`${ROUTES.ONBOARDING.MAIN}/*`);
    const fixedBlock = useAppSelector(onBoardingFixedBlock);
    const height = fixedBlock ? fixedBlock?.offsetHeight : 0;
    const chatClassName = useChatClassName();
    const chatUi = useMemo(() => {
        const { pathname } = location;
        if (authorizedId) {
            return (
                <>
                    <div className="text-15 leading-[140%] -mt-[8px] block md:hidden">
                        <button className={`underline font-dmSansBold text-15 leading-[140%] ${chatClassName}`} data-testid="onboarding-footer-questions-call-link">Chat with Us</button>
                    </div>
                    <div className="text-14 leading-[140%] block md:hidden">
                        <span data-testid="onboarding-footer-questions-description">Chat with a MedBox Care Advisor, and we’ll be happy to assist you with anything you need.</span>
                    </div>
                    <p className="text-14 leading-[140%] hidden md:block">
                        <button className={`underline font-dmSansBold text-15 leading-[140%] ${chatClassName}`} data-testid="onboarding-footer-questions-call-link">Chat with a MedBox Care Advisor,</button> and we’ll be happy to assist you with anything you need.
                    </p>
                </>
            )
        } else if (pathname.includes('login')) {
            return (
                <div className="text-14 leading-[140%] block">
                    <span data-testid="onboarding-footer-questions-description">Log in to chat with a MedBox Care Advisor</span>
                </div>
            )
        } else {
            return (
                <div className="text-14 leading-[140%] block">
                    <span data-testid="onboarding-footer-questions-description">Sign up to chat with a MedBox Care Advisor</span>
                </div>
            )
        }
    }, [location, authorizedId, chatClassName]);

    return (
        <div className="relative flex flex-col !bg-inherit" id="footer" data-testid="onboarding-footer-container">
            <div className={`flex h-[55px] w-full items-end ${ivoryBg ? 'bg-[#f8f5f0]' : ''}`}>
                {!isMobile && <img src={FooterBg} alt="" className="relative z-[1] max-h-[55px] w-full" />}
                {isMobile && <img src={FooterMobileBg} alt="" className="relative z-[1] max-h-[55px] w-full" />}
            </div>

            <div
                className="relative bg-primary-700 text-white"
                id="footer"
                style={{
                    paddingBottom: isOnboarding && isMobile ? height + 48 + 'px' : ''
                }}
            >
                <div className="2xl:mt-24 relative z-10 mx-auto mt-12 flex h-full flex-col justify-between gap-[30px] bg-primary-700 px-5 pb-[20px] lg:mt-16 lg:flex-row lg:gap-10 lg:pb-16 xl:!px-0 xl:w-[1180px] lx:px-8 2xl:w-[1340px] 2xl:!px-0 3xl:w-[1500px] 3xl:!px-0 4xl:w-[1680px] 4xl:!px-0">

                    <FooterInfoItem icon={Chat} iconAlt="Phone icon" title="Have Any Questions?" titleTestId="onboarding-footer-questions-title">
                        {chatUi}
                    </FooterInfoItem>

                    <FooterInfoItem icon={MoneyHand} iconAlt="Phone icon" title="How Much are MedBox Copays?" titleTestId="onboarding-footer-copays-title">
                        <p className="text-14 leading-[140%]" data-testid="onboarding-footer-copays-description">
                            Your copays will stay the same as they are at your local retail pharmacy for a 30-day supply of medication.
                        </p>
                    </FooterInfoItem>

                    <FooterInfoItem icon={CheckVerified} iconAlt="Checkmark icon" title="MedBox is Free" titleTestId="onboarding-footer-free-title">
                        <p className="text-14 leading-[140%]" data-testid="onboarding-footer-free-description">
                            MedBox pill dispenser, pill sorting, and delivery is free.
                        </p>
                    </FooterInfoItem>
                </div>
                <div className="w-full border-t border-primary py-[20px]">
                    <div className="flex flex-col 2xl:flex-row  items-start 2xl:items-center justify-center gap-4 text-center p-[10px] text-12 px-[20px] lx:px-[30px] font-atkinson text-mint-400">
                        {/* <div id="teconsent"></div> */}
                        <span>
                            © 2025 MedBox by AmeriPharma. All Rights Reserved.
                        </span>
                        <div className="flex flex-row flex-wrap justify-start items-center gap-3 lg:gap-0">
                            {links.map((link, index) => (
                                <div key={link.label} className="flex items-center">
                                    {link.external ? (
                                        <a
                                            href={link.link}
                                            className={`flex items-center gap-1 border-b border-transparent hover:border-current ${link.customClass || ''}`}
                                        >
                                            {link.label}
                                            {link.img && (
                                                <img
                                                    src={link.img}
                                                    alt="icon"
                                                    className="w-[22px] h-[12px]"
                                                />
                                            )}
                                        </a>
                                    ) : (
                                        <Link
                                            to={link.link}
                                            className="border-b border-transparent hover:border-current flex items-center gap-1"
                                            target="_blank"
                                        >
                                            {link.label}
                                            {link.img && (
                                                <img
                                                    src={link.img}
                                                    alt={`${link.label} icon`}
                                                    className="w-[22px] h-[12px]"
                                                />
                                            )}
                                        </Link>
                                    )}
                                    {index !== links.length - 1 && (
                                        <span className="hidden lg:inline mx-2 text-white">•</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Footer;
